// ============================================
// CONFIGURAÇÃO DE PERMISSÕES E PERFIS DE ACESSO
// Sistema de controle de acesso para clínicas de saúde
// ============================================

import { isMasterSupportUser } from '../constants/masterSupport';

export type SystemRole =
  | 'admin'
  | 'admin_master'
  | 'professional'
  | 'receptionist'
  | 'financial'
  | 'assistant';

// ============================================
// LABELS E CORES POR PERFIL
// ============================================

export const ROLE_LABELS: Record<SystemRole, string> = {
  admin: 'Administrador',
  admin_master: 'Administrador Master',
  professional: 'Profissional',
  receptionist: 'Recepcionista',
  financial: 'Financeiro',
  assistant: 'Auxiliar',
};

export const ROLE_DESCRIPTIONS: Record<SystemRole, string> = {
  admin:
    'Configurações e módulos administrativos amplos; sem Gestão de usuários, sem Resumo/DRE consolidado, sem Extratos e sem Relatórios por padrão (Relatórios só se liberado no cadastro do usuário).',
  admin_master:
    'Único perfil para Gestão de usuários / logs relacionados no painel. Resumo, DRE, plano de contas e extratos também para o perfil Financeiro.',
  professional:
    'Profissional de saúde (médico, dentista, enfermeiro etc.). Acesso aos seus pacientes, agenda, prescrições e módulos clínicos.',
  receptionist:
    'Gerencia agendamentos, recepção, sala de espera, CRM (funis, colunas e etiquetas), receituários, orçamentos e financeiro operacional (sem evoluções clínicas, prontuário SOAP nem DRE/Extratos).',
  financial:
    'Financeiro completo: contas, caixa, DRE, extratos/conciliação, resumo e relatórios. Agenda só formulário do agendamento (sem prontuário clínico).',
  assistant: 'Suporte clínico limitado. Auxilia com prontuários, pacientes e sala de espera.',
};

export const ROLE_COLORS: Record<SystemRole, { bg: string; color: string; border: string }> = {
  admin: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  admin_master: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  professional: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  receptionist: { bg: '#fefce8', color: '#ca8a04', border: '#fef08a' },
  financial: { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  assistant: { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
};

/** Perfis que podem ter vínculo opcional com um cadastro em clinic_professionals. */
export function roleSupportsProfessionalLink(role?: SystemRole | string | null): boolean {
  const r = String(role || '');
  return (
    r === 'professional' ||
    r === 'assistant' ||
    r === 'admin' ||
    r === 'admin_master'
  );
}

// ============================================
// PERMISSÕES POR ROTA
// ============================================

/** Rotas só para administrador_master (Gestão de usuários no SPA). */
/** Outro Administrador Master protegido quando o backend ativa isolamento por ambiente. */
export function isProtectedAdminMasterPeer(
  currentUser: { id: string; role: SystemRole } | null | undefined,
  target: { id: string; role: SystemRole } | null | undefined,
  isolationEnabled: boolean,
): boolean {
  if (!isolationEnabled || !currentUser || !target) return false;
  if (currentUser.role !== 'admin_master' || target.role !== 'admin_master') return false;
  return currentUser.id !== target.id;
}

export function isUserManagementRestrictedPath(normalizedPath: string): boolean {
  const base = normalizedPath.replace(/[/]+$/, '') || '/';
  return base === '/user-management' || base.startsWith('/user-management/');
}

/** Pagamento da mensalidade da plataforma (PIX) — somente Admin Master. */
export function isPlatformPlanPaymentsPath(normalizedPath: string): boolean {
  const base = normalizedPath.replace(/[/]+$/, '') || '/';
  return base === '/platform-payments' || base.startsWith('/platform-payments/');
}

export const ROUTE_PERMISSIONS: Record<string, SystemRole[]> = {
  // Dashboards
  '/dashboard': ['admin', 'admin_master'],
  // Entrada /clinic sem subpath → redireciona para agenda (sem página dedicada)
  '/clinic': ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'],
  
  // CRM & Mensagens — recepção opera o funil no dia a dia (dona da próxima ação)
  '/crm': ['admin', 'admin_master', 'receptionist'],
  '/crm/config': ['admin', 'admin_master', 'receptionist'],
  '/crm/campaigns': ['admin', 'admin_master', 'receptionist'],
  '/inbox': ['admin', 'admin_master', 'receptionist'],

  // Chat interno: comunicação da equipe, liberada para todos os perfis
  '/team-chat': ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'],
  
  // Módulos clínicos principais
  '/clinic/patients': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  '/clinic/budgets': ['admin', 'admin_master', 'professional', 'receptionist', 'financial'],
  '/clinic/daily-audit': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/agenda': ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'],
  '/tutoriais': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  '/clinic/medical-reports': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  '/clinic/doctoralia': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  
  // Clínica - submenu
  '/clinic/medical-records': ['admin', 'admin_master', 'professional', 'assistant'],
  '/clinic/procedures': ['admin', 'admin_master', 'professional', 'receptionist'],
  // Planos: a recepção vende e o financeiro acompanha as mensalidades
  '/clinic/patient-plans': ['admin', 'admin_master', 'professional', 'receptionist', 'financial'],
  '/clinic/professionals': ['admin', 'admin_master', 'receptionist'],
  '/clinic/inventory': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/items': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/categories': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/attributes': ['admin', 'admin_master', 'financial'],
  '/inventory/product-lines': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/locations': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/manufacturers': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/suppliers': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/kits': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/insurance-medications': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/movements': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/requisitions': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/consigned': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/operations': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/inventory/pending': ['admin', 'admin_master', 'receptionist', 'financial'],
  '/clinic/evolutions': ['admin', 'admin_master', 'professional', 'assistant'],
  '/clinic/prescriptions': ['admin', 'admin_master', 'professional', 'receptionist'],
  '/clinic/physiotherapy-assessments': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  '/clinic/exam-orders': ['admin', 'admin_master', 'professional'],
  '/clinic/laboratory': ['admin', 'admin_master', 'professional'],
  '/clinic/certificates': ['admin', 'admin_master', 'professional'],
  '/clinic/referrals': ['admin', 'admin_master', 'professional'],
  '/clinic/insurance': ['admin', 'admin_master', 'financial'],
  '/clinic/waiting-room': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  '/clinic/image-gallery': ['admin', 'admin_master', 'professional', 'assistant'],
  '/clinic/treatment-plan': ['admin', 'admin_master', 'professional'],
  '/clinic/plannings': ['admin', 'admin_master', 'professional', 'receptionist'],
  '/clinic/planning-templates': ['admin', 'admin_master'],
  '/clinic/odontogram': ['admin', 'admin_master', 'professional', 'assistant'],
  '/clinic/periodontal-charts': ['admin', 'admin_master', 'professional', 'assistant'],
  '/clinic/dental-treatments': ['admin', 'admin_master', 'professional', 'assistant'],
  '/clinic/reports': ['admin_master', 'financial'],
  '/clinic/reports/meta-leads': ['admin_master', 'financial'],
  '/clinic/anamnesis-templates': ['admin', 'admin_master', 'professional'],
  '/clinic/reminder-settings': ['admin', 'admin_master', 'receptionist'],
  '/clinic/birthdays': ['admin', 'admin_master', 'receptionist'],
  '/clinic/digital-signature': ['admin', 'admin_master', 'professional'],
  // A recepção coleta a assinatura do contrato no balcão — precisa enxergar a tela.
  '/clinic/document-signing': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  '/clinic/contract-templates': ['admin', 'admin_master'],
  '/clinic/settings': ['admin', 'admin_master'],
  '/financial/dashboard': ['admin_master', 'financial'],
  '/financial/chart-of-accounts': ['admin_master', 'financial'],
  '/financial/dre': ['admin_master', 'financial'],
  '/financial/dre/snapshots': ['admin_master', 'financial'],
  '/financial/accounts-payable': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/nfe-received': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/accounts-receivable': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/cash-register': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/audit': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/bank-statements': ['admin_master', 'financial'],
  '/financial/repasses': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/tef': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/nfse': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/nfse-companies': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/receipts': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/checks-received': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/checks-issued': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/card-settlements': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/insurance-receivables': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/pending-discounts': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/professional-fees': ['admin', 'admin_master', 'financial', 'receptionist'],
  
  // Gestão de usuários — somente Administrador Master
  '/user-management': ['admin_master'],

  // Mensalidade da plataforma (PIX)
  '/platform-payments': ['admin_master'],

  // Painel central da Klinvia — só existe no deploy interno (PLATFORM_ADMIN_ENABLED)
  '/platform-clients': ['admin_master'],
  '/platform-support': ['admin_master'],
  '/client-management': ['admin_master'],
  '/platform-crm-forms': ['admin_master'],

  // Administração da clínica (multi-unidade)
  '/admin/units': ['admin', 'admin_master'],
  
  // WhatsApp / Config
  '/whatsapp-auto': ['admin', 'admin_master'],
  '/whatsapp-dashboard': ['admin', 'admin_master'],
  '/whatsapp-qr': ['admin', 'admin_master'],
  '/whatsapp-manual': ['admin', 'admin_master'],
  '/whatsapp-proxy': ['admin', 'admin_master'],
  '/whatsapp-cloud-api': ['admin', 'admin_master'],

  // Central de Atendimento
  '/control': ['admin', 'admin_master'],
  '/support': ['admin', 'admin_master'],
  '/central-ia': ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'],
  '/abandoned-cart': ['admin', 'admin_master'],
  
  // Outros
  '/kanban': ['admin', 'admin_master'],
  '/followups': ['admin', 'admin_master'],
  '/campaigns': ['admin', 'admin_master'],
  '/call-templates': ['admin', 'admin_master'],
  '/automations': ['admin', 'admin_master'],
  '/attendants': ['admin', 'admin_master'],
  '/settings': ['admin', 'admin_master'],
};

// ============================================
// ROTA PADRÃO POR PERFIL
// ============================================

export const DEFAULT_ROUTE: Record<SystemRole, string> = {
  admin: '/agenda',
  admin_master: '/agenda',
  professional: '/agenda',
  receptionist: '/agenda',
  financial: '/financial/accounts-payable',
  assistant: '/agenda',
};

// ============================================
// FUNÇÕES AUXILIARES
// ============================================

/**
 * Pode excluir agendamentos e lançamentos financeiros?
 * Apenas Administrador Master ou gestor da unidade ativa.
 */
/** Evoluções clínicas (SOAP) — recepção não acessa; opcionalmente só com profissional vinculado. */
export function canAccessClinicalEvolutions(
  role: SystemRole | null | undefined,
  opts?: {
    professionalId?: string | null;
    hideWithoutProfessional?: boolean;
  },
): boolean {
  if (!role) return false;
  if (role === 'receptionist') return false;
  if (opts?.hideWithoutProfessional && !opts.professionalId) return false;
  return true;
}

/** Receituários — recepção pode emitir e consultar. */
export function canAccessPrescriptions(role: SystemRole | null | undefined): boolean {
  if (!role) return false;
  return ['admin', 'admin_master', 'professional', 'receptionist'].includes(role);
}

export function canDeleteProtectedRecords(
  role: SystemRole | null | undefined,
  currentUnit?: { can_manage?: boolean } | null,
): boolean {
  if (role === 'admin_master') return true;
  return Boolean(currentUnit?.can_manage);
}

/**
 * Pode ajustar manualmente o status de conformidade na auditoria diária?
 * Administrador, Administrador Master ou gestor da unidade ativa.
 */
export function canOverrideDailyAuditCompliance(
  role: SystemRole | null | undefined,
  currentUnit?: { can_manage?: boolean } | null,
): boolean {
  if (role === 'admin_master' || role === 'admin') return true;
  return Boolean(currentUnit?.can_manage);
}

/**
 * Pode estornar lançamento no caixa?
 * - Caixa aberto: operador do caixa ou gestor / Administrador Master.
 * - Caixa fechado: somente Administrador Master.
 */
export function canReverseCashRegisterMovement(
  role: SystemRole | null | undefined,
  currentUnit?: { can_manage?: boolean } | null,
  options?: {
    registerStatus?: string | null;
    registerOpenedBy?: string | null;
    userId?: string | null;
  },
): boolean {
  if (options?.registerStatus === 'closed') return role === 'admin_master';
  if (options?.registerStatus !== 'open') return false;
  const isOperator = Boolean(
    options?.userId
    && options?.registerOpenedBy
    && String(options.userId) === String(options.registerOpenedBy),
  );
  if (isOperator) return true;
  return canDeleteProtectedRecords(role, currentUnit);
}

/**
 * Pode editar lançamento no caixa?
 * - Caixa aberto: operador do caixa ou gestor / Administrador Master.
 * - Caixa fechado: somente Administrador Master.
 */
export function canEditCashRegisterMovement(
  role: SystemRole | null | undefined,
  currentUnit?: { can_manage?: boolean } | null,
  options?: {
    registerStatus?: string | null;
    registerOpenedBy?: string | null;
    userId?: string | null;
  },
): boolean {
  if (options?.registerStatus === 'closed') return role === 'admin_master';
  if (options?.registerStatus !== 'open') return false;
  return canReverseCashRegisterMovement(role, currentUnit, options);
}

/**
 * Pode excluir lançamento com caixa fechado?
 * Somente Administrador Master.
 */
export function canDeleteClosedCashRegisterMovement(
  role: SystemRole | null | undefined,
  registerStatus?: string | null,
): boolean {
  if (registerStatus !== 'closed') return false;
  return role === 'admin_master';
}

export const CUSTOM_PERMISSION_CAN_ACCESS_REPORTS = 'can_access_reports';
export const CUSTOM_PERMISSION_CAN_ACCESS_INVENTORY = 'can_access_inventory';
/** Admin master vinculado a profissional: modal da agenda no modo clínico (painel do paciente, sem “Editar consulta”). */
export const CUSTOM_PERMISSION_AGENDA_PROFESSIONAL_APPOINTMENT_VIEW =
  'agenda_professional_appointment_view';
/** Agenda consolidada de todas as unidades ativas (multiclinica / hub). */
export const CUSTOM_PERMISSION_AGENDA_SEE_ALL_UNITS = 'agenda_see_all_units';

/**
 * Ao abrir um agendamento existente na agenda, usa o fluxo do profissional (só painel clínico).
 * Profissionais com vínculo: sempre. Admin master: só com a permissão acima.
 */
export function userAgendaProfessionalAppointmentView(user: {
  role: SystemRole;
  professional_id?: string | null;
  custom_permissions?: Record<string, unknown> | null;
} | null | undefined): boolean {
  if (!user?.professional_id) return false;
  if (user.role === 'professional') return true;
  if (user.role === 'admin_master') {
    return user.custom_permissions?.[CUSTOM_PERMISSION_AGENDA_PROFESSIONAL_APPOINTMENT_VIEW] === true;
  }
  return false;
}

const INVENTORY_MODULE_ROLES: SystemRole[] = ['admin', 'admin_master', 'receptionist', 'financial'];

export function userCanAccessInventoryModule(user: {
  role: SystemRole;
  custom_permissions?: Record<string, unknown> | null;
}): boolean {
  if (INVENTORY_MODULE_ROLES.includes(user.role)) return true;
  if (user.role === 'professional') {
    return user.custom_permissions?.[CUSTOM_PERMISSION_CAN_ACCESS_INVENTORY] === true;
  }
  return false;
}

export function isInventoryModuleRoute(path: string): boolean {
  const pathOnly = (path.split('#')[0].split('?')[0] || '/').trim() || '/';
  const base = pathOnly.replace(/[/]+$/, '') || '/';
  if (base === '/clinic/inventory' || base.startsWith('/clinic/inventory/')) return true;
  if (base === '/inventory' || base.startsWith('/inventory/')) return true;
  return false;
}

export function userCanAccessClinicReports(user: {
  role: SystemRole;
  custom_permissions?: Record<string, unknown> | null;
}): boolean {
  if (user.role === 'admin_master' || user.role === 'financial') return true;
  if (user.role === 'admin') {
    return user.custom_permissions?.[CUSTOM_PERMISSION_CAN_ACCESS_REPORTS] === true;
  }
  return false;
}

export function isClinicAnalyticsReportsPath(path: string): boolean {
  const pathOnly = (path.split('#')[0].split('?')[0] || '/').trim() || '/';
  const base = pathOnly.replace(/[/]+$/, '') || '/';
  if (base === '/clinic/reports') return true;
  return base.startsWith('/clinic/reports/');
}

export interface RoutePermissionUser {
  id?: string;
  role: SystemRole;
  custom_permissions?: Record<string, unknown> | null;
  current_unit?: {
    can_manage?: boolean;
    allowed_whatsapp_inbox_paths?: string[] | null;
  } | null;
  session_flags?: {
    whatsapp_inbox_user_access_enabled?: boolean;
  };
  professional?: {
    metadata?: Record<string, unknown> | null;
    can_access_dynamic_inbox?: boolean;
  } | null;
}

const WHATSAPP_CONFIG_ROUTE_PREFIXES = [
  '/whatsapp-auto',
  '/whatsapp-dashboard',
  '/whatsapp-qr',
  '/whatsapp-manual',
  '/whatsapp-proxy',
  '/whatsapp-cloud-api',
];

/** Config WhatsApp no menu: admin ou recepcionista gestora da unidade ativa. */
export function canAccessWhatsAppConfig(
  role: SystemRole | null | undefined,
  currentUnit?: { can_manage?: boolean } | null,
): boolean {
  if (!role) return false;
  if (role === 'admin' || role === 'admin_master') return true;
  if (role === 'receptionist' && Boolean(currentUnit?.can_manage)) return true;
  return false;
}

function isWhatsAppConfigRoute(path: string): boolean {
  const pathOnly = (path.split('#')[0].split('?')[0] || '/').trim() || '/';
  return WHATSAPP_CONFIG_ROUTE_PREFIXES.some(
    (prefix) => pathOnly === prefix || pathOnly.startsWith(`${prefix}/`),
  );
}

/** Rotas fixas da Central — profissionais com switch só acessam inboxes dinâmicos (slug customizado). */
const RESERVED_INBOX_SLUGS = new Set([
  'whatsapp',
  'whatsapp-recovery',
  'whatsapp-cloud-api',
  'telegram',
  'instagram',
  'sms',
  'webchat',
  'voice',
]);

export function isDynamicUnitInboxPath(path: string): boolean {
  const base = (path.split('#')[0].split('?')[0] || '/').replace(/\/+$/, '') || '/';
  if (base === '/inbox') return false;
  if (!base.startsWith('/inbox/')) return false;
  const slug = base.slice('/inbox/'.length);
  if (!slug || slug.includes('/')) return false;
  return !RESERVED_INBOX_SLUGS.has(slug);
}

export function userCanAccessWhatsappInboxPath(user: RoutePermissionUser, path: string): boolean {
  if (!user.session_flags?.whatsapp_inbox_user_access_enabled) return true;
  if (isMasterSupportUser(user.id)) return true;
  const base = (path.split('#')[0].split('?')[0] || '/').replace(/\/+$/, '') || '/';
  const allowed = user.current_unit?.allowed_whatsapp_inbox_paths;
  if (allowed == null) return true;
  if (allowed.length === 0) return false;
  return allowed.includes(base);
}

export function professionalCanAccessDynamicInboxFromUser(user?: RoutePermissionUser | null): boolean {
  if (!user?.professional) return false;
  if (user.professional.can_access_dynamic_inbox === true) return true;
  const metadata = user.professional.metadata;
  if (!metadata || typeof metadata !== 'object') return false;
  return metadata.can_access_dynamic_inbox === true;
}

/**
 * Verifica se um perfil tem acesso a uma rota
 */
export function hasRoutePermission(userRole: SystemRole, path: string): boolean {
  const pathOnly = (path.split('#')[0].split('?')[0] || '/').trim() || '/';

  if (isUserManagementRestrictedPath(pathOnly)) {
    return userRole === 'admin_master';
  }

  if (isPlatformPlanPaymentsPath(pathOnly)) {
    return userRole === 'admin_master';
  }

  if (ROUTE_PERMISSIONS[pathOnly]) {
    return ROUTE_PERMISSIONS[pathOnly].includes(userRole);
  }

  const matchingRoute = Object.keys(ROUTE_PERMISSIONS)
    .filter(route => pathOnly.startsWith(route))
    .sort((a, b) => b.length - a.length)[0];

  if (matchingRoute) {
    return ROUTE_PERMISSIONS[matchingRoute].includes(userRole);
  }

  const superRoles: SystemRole[] = ['admin', 'admin_master'];
  return superRoles.includes(userRole);
}

/**
 * Verifica permissão considerando flags do usuário (ex.: profissional com inbox dinâmico).
 */
export function hasUserRoutePermission(user: RoutePermissionUser, path: string): boolean {
  const pathOnly = (path.split('#')[0].split('?')[0] || '/').trim() || '/';

  if (isWhatsAppConfigRoute(pathOnly)) {
    return canAccessWhatsAppConfig(user.role, user.current_unit);
  }

  if (pathOnly === '/inbox' || pathOnly.startsWith('/inbox/')) {
    if (user.role === 'professional') {
      if (!professionalCanAccessDynamicInboxFromUser(user)) return false;
      if (pathOnly === '/inbox' || !isDynamicUnitInboxPath(pathOnly)) return false;
      if (!userCanAccessWhatsappInboxPath(user, pathOnly)) return false;
      return true;
    }
    if (pathOnly.startsWith('/inbox/') && !userCanAccessWhatsappInboxPath(user, pathOnly)) {
      return false;
    }
  }

  if (isClinicAnalyticsReportsPath(pathOnly)) {
    return userCanAccessClinicReports(user);
  }

  if (isInventoryModuleRoute(pathOnly)) {
    return userCanAccessInventoryModule(user);
  }

  return hasRoutePermission(user.role, pathOnly);
}

/**
 * Retorna a rota padrão para um perfil
 */
export function getDefaultRoute(userRole: SystemRole): string {
  return DEFAULT_ROUTE[userRole] || '/agenda';
}

/**
 * Retorna o label de um perfil
 */
export function getRoleLabel(role: SystemRole): string {
  return ROLE_LABELS[role] || role;
}

/**
 * Retorna as cores de um perfil
 */
export function getRoleColors(role: SystemRole): { bg: string; color: string; border: string } {
  return ROLE_COLORS[role] || ROLE_COLORS.assistant;
}

/**
 * Lista todos os perfis disponíveis
 */
export function getAllRoles(): { value: SystemRole; label: string; description: string }[] {
  return (Object.keys(ROLE_LABELS) as SystemRole[]).map(role => ({
    value: role,
    label: ROLE_LABELS[role],
    description: ROLE_DESCRIPTIONS[role],
  }));
}
